import React, { useState } from 'react';
import { Table } from 'antd';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { AreaSlaData, DriverSlaData, WhsSlaData, DspSlaData } from '../types';
import { DIMENSION_WHS, DIMENSION_DSP, DIMENSION_AREA, DIMENSION_DRIVER } from '../constants';
import {
    LEVEL1_META,
    LEVEL1_ORDER,
    LEVEL2_BY_LEVEL1,
    LEVEL2_META,
    encodeUndeliveredAnomaly,
    getLevel1Label,
    getLevel2LabelInGroup,
    undeliveredTotal,
} from '../undeliveredAttribution';

dayjs.extend(isSameOrAfter);

type SlaTableRow = AreaSlaData | DriverSlaData | WhsSlaData | DspSlaData;

interface SlaMonitoringTableProps {
    data: SlaTableRow[];
    viewMode?: 'driver' | 'area' | 'dsp' | 'whs';
    onDetailClick: (type: string, record: SlaTableRow) => void;
    onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    loading?: boolean;
    pagination?: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
        hasPrevious: boolean;
    };
    /** 为 true 时不展示 T+1～T+5 领取妥投率列（如 Hub 页） */
    hideT1ToT5Rates?: boolean;
}

const SlaMonitoringTable: React.FC<SlaMonitoringTableProps> = ({
    data,
    viewMode = DIMENSION_AREA,
    onDetailClick,
    onSortChange,
    loading = false,
    pagination,
    hideT1ToT5Rates = false
}) => {
    const { t } = useTranslation();
    // 排序状态管理：默认按整体转化率从高到低
    const [sortConfig, setSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallConversionRate',
        sortDirection: 'DESC'
    });

    // 处理排序点击
    const handleSortClick = (field: string) => {
        let newDirection: 'ASC' | 'DESC' = 'ASC'; // 首次点击默认为升序
        
        // 如果点击的是当前排序字段，则切换排序方向
        if (sortConfig.sortBy === field) {
            newDirection = sortConfig.sortDirection === 'DESC' ? 'ASC' : 'DESC';
        }
        
        const newSortConfig = {
            sortBy: field,
            sortDirection: newDirection
        };
        
        setSortConfig(newSortConfig);
        onSortChange?.(field, newDirection);
    };

    // 可排序列：已到货、整体转化率、领取率、领取妥投率；再加 T+1～T+5领取妥投率；Hub 页隐藏 T+1～T+5
    const showT1ToT5 = !hideT1ToT5Rates;
    const SORTABLE_FIELDS = showT1ToT5
        ? ['actualArrivals', 'overallConversionRate', 'pickupRate', 'latestDeliveryRate', 't1DeliveryRate', 't2DeliveryRate', 't3DeliveryRate', 't4DeliveryRate', 't5DeliveryRate']
        : ['actualArrivals', 'overallConversionRate', 'pickupRate', 'latestDeliveryRate'];

    const renderSortIcon = (field: string) => {
        if (!SORTABLE_FIELDS.includes(field)) return null;
        if (sortConfig.sortBy !== field) {
            return <SortDescendingOutlined className="ml-1 cursor-pointer text-gray-400" />;
        }
        return sortConfig.sortDirection === 'DESC'
            ? <SortAscendingOutlined className="ml-1 cursor-pointer text-blue-500" />
            : <SortDescendingOutlined className="ml-1 cursor-pointer text-blue-500" />;
    };

    const renderSortTitle = (field: string, title: string) => (
        SORTABLE_FIELDS.includes(field) ? (
            <span>
                {title}
                <span onClick={() => handleSortClick(field)}>{renderSortIcon(field)}</span>
            </span>
        ) : title
    );

    // 计算领取妥投率进度条颜色
    // 规则：<30% 红色；30% <= DSR < 70% 黄色；70% <= DSR < 100% 蓝色；=100% 绿色
    const getDeliveryRateColor = (rate: number): string => {
        if (rate === 100) {
            return '#52c41a'; // 绿色
        } else if (rate >= 70) {
            return '#1890ff'; // 蓝色
        } else if (rate >= 30) {
            return '#faad14'; // 黄色
        } else {
            return '#ff4d4f'; // 红色
        }
    };

    /** 越高越差：未完结/未处理率进度条配色 */
    const getBadMetricBarColor = (rate: number): string => {
        const r = Number.isFinite(rate) ? Math.max(0, rate) : 0;
        if (r <= 0) return '#52c41a';
        if (r < 10) return '#1890ff';
        if (r < 30) return '#faad14';
        return '#ff4d4f';
    };

    const renderBadRateBar = (rate: number, onDrill?: () => void) => {
        const safeRate = Number.isFinite(rate) ? Math.max(0, Math.min(100, rate)) : 0;
        const rateText = safeRate.toFixed(2);
        const clickable = Boolean(onDrill) && safeRate > 0;
        return (
            <div
                role={clickable ? 'button' : undefined}
                onClick={clickable ? (e) => { e.preventDefault(); onDrill?.(); } : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrill?.(); } } : undefined}
                tabIndex={clickable ? 0 : undefined}
                style={{
                position: 'relative',
                width: '60px',
                height: '24px',
                background: '#f0f0f0',
                borderRadius: '12px',
                overflow: 'hidden',
                margin: 0,
                cursor: clickable ? 'pointer' : 'default'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${safeRate}%`,
                    background: getBadMetricBarColor(safeRate),
                    borderRadius: '12px',
                    transition: 'width 0.3s ease'
                }} />
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#333',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 1
                }}>
                    {rateText}%
                </div>
            </div>
        );
    };

    // 通用率进度条（整体转化率、领取率、最新领取妥投率、T+N领取妥投率共用）
    const renderRateBar = (rate: number) => {
        const safeRate = Number.isFinite(rate) ? Math.max(0, Math.min(100, rate)) : 0;
        const rateText = safeRate.toFixed(2);
        return (
            <div style={{
                position: 'relative',
                width: '60px',
                height: '24px',
                background: '#f0f0f0',
                borderRadius: '12px',
                overflow: 'hidden',
                margin: 0
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${safeRate}%`,
                    background: getDeliveryRateColor(safeRate),
                    borderRadius: '12px',
                    transition: 'width 0.3s ease'
                }} />
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#333',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 1
                }}>
                    {rateText}%
                </div>
            </div>
        );
    };

    // 判断是否应该显示T+N领取妥投率
    // 规则：当前自然日期 >= 到仓日期 + N天时，才显示数据
    // 举例：到仓日期10-31日，T+1日为11-01日
    // 在11-01 00:00:00-23:59:59内查看时，只有T+1妥投有值
    const shouldShowTNDeliveryRate = (record: SlaTableRow, n: number): boolean => {
        const arrivalDate = record.arrivalDate;
        if (!arrivalDate) {
            return false;
        }
        const arrivalDateObj = dayjs(arrivalDate).startOf('day'); // 到仓日期的00:00:00
        const targetDate = arrivalDateObj.add(n, 'day').startOf('day'); // T+N日期的00:00:00
        const currentDate = dayjs().startOf('day'); // 当前自然日期的00:00:00
        return currentDate.isSameOrAfter(targetDate);
    };

    // 渲染T+N领取妥投率（带日期判断）
    const renderTNDeliveryRate = (rate: number, record: SlaTableRow, n: number) => {
        const shouldShow = shouldShowTNDeliveryRate(record, n);
        if (!shouldShow) return <span>-</span>;
        return renderRateBar(rate);
    };

    // WHS 维度：仅展示 WHS 列，不展示 DSP / 司机 / 区域列
    // DSP 维度：仅展示 DSP 列，不展示 WHS / 司机 / 区域列
    // area / driver 维度：展示 DSP + 区域 或 DSP + 司机
    const dimensionCols = viewMode === DIMENSION_WHS
        ? [
            {
                title: t('wms.delivery.dailyTaskMonitoring.buttons.whs'),
                dataIndex: 'warehouseName',
                key: 'warehouseName',
                width: 100,
                align: 'left' as const,
                fixed: 'left' as const,
                ellipsis: false,
                render: (text: string, record: SlaTableRow) => (
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {'warehouseName' in record ? ((record as WhsSlaData).warehouseName || '-') : '-'}
                    </span>
                )
            }
        ]
        : viewMode === DIMENSION_DSP
        ? [
            {
                title: t('wms.delivery.parcelSlaMonitoring.columns.dsp'),
                dataIndex: 'dsp',
                key: 'dsp',
                width: 80,
                align: 'left' as const,
                fixed: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
            }
        ]
        : [
            {
                title: t('wms.delivery.parcelSlaMonitoring.columns.dsp'),
                dataIndex: 'dsp',
                key: 'dsp',
                width: 80,
                align: 'left' as const,
                fixed: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
            },
            viewMode === DIMENSION_DRIVER
                ? {
                    title: t('wms.delivery.parcelSlaMonitoring.detailColumns.driver'),
                    dataIndex: 'driverName',
                    key: 'driverName',
                    width: 100,
                    align: 'left' as const,
                    fixed: 'left' as const,
                    ellipsis: false,
                    render: (text: string, record: SlaTableRow) => (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {'driverName' in record ? (record.driverName || '-') : '-'}
                        </span>
                    )
                }
                : {
                    title: t('wms.delivery.parcelSlaMonitoring.columns.area'),
                    dataIndex: 'area',
                    key: 'area',
                    width: 50,
                    align: 'left' as const,
                    fixed: 'left' as const,
                    ellipsis: false,
                    render: (text: string) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
                }
        ];

    const columns = [
        {
            title: t('wms.delivery.parcelSlaMonitoring.columns.serialNumber'),
            key: 'serialNumber',
            width: 60,
            align: 'left' as const,
            fixed: 'left' as const,
            render: (_: any, __: any, index: number) => {
                const currentPage = pagination?.page || 1;
                const pageSize = pagination?.size || 10;
                return (currentPage - 1) * pageSize + index + 1;
            }
        },
        ...dimensionCols,
        {
            title: renderSortTitle('actualArrivals', t('wms.delivery.parcelSlaMonitoring.columns.actualArrivals')),
            dataIndex: 'actualArrivals',
            key: 'actualArrivals',
            width: 78,
            align: 'left' as const,
            render: (value: number, record: SlaTableRow) => (
                value > 0 ? (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onDetailClick('actual_arrivals', record);
                        }}
                        style={{ color: '#1890ff' }}
                    >
                        {value}
                    </a>
                ) : (
                    <span>{value}</span>
                )
            )
        },
        {
            title: t('wms.delivery.parcelSlaMonitoring.columns.pickedCount'),
            dataIndex: 'pickedCount',
            key: 'pickedCount',
            width: 60,
            align: 'left' as const,
            render: (value: number, record: SlaTableRow) => (
                value > 0 ? (
                    <a href="#" onClick={(e) => { e.preventDefault(); onDetailClick('picked', record); }} style={{ color: '#1890ff' }}>{value}</a>
                ) : (
                    <span>{value || 0}</span>
                )
            )
        },
        {
            title: t('wms.delivery.parcelSlaMonitoring.columns.deliveredLatest'),
            dataIndex: 'deliveredLatest',
            key: 'deliveredLatest',
            width: 60,
            align: 'left' as const,
            render: (value: number, record: SlaTableRow) => (
                value > 0 ? (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onDetailClick('delivered_latest', record);
                        }}
                        style={{ color: '#1890ff' }}
                    >
                        {value}
                    </a>
                ) : (
                    <span>{value}</span>
                )
            )
        },
        {
            title: renderSortTitle('overallConversionRate', t('wms.delivery.parcelSlaMonitoring.columns.overallConversionRate')),
            key: 'overallConversionRate',
            width: 110,
            align: 'left' as const,
            render: (_: any, record: SlaTableRow) => {
                const rate = record.actualArrivals > 0
                    ? (record.deliveredLatest / record.actualArrivals) * 100
                    : 0;
                return renderRateBar(rate);
            }
        },
        {
            title: renderSortTitle('pickupRate', t('wms.delivery.parcelSlaMonitoring.columns.pickupRate')),
            key: 'pickupRate',
            width: 80,
            align: 'left' as const,
            render: (_: any, record: SlaTableRow) => {
                const rate = record.actualArrivals > 0
                    ? (record.pickedCount / record.actualArrivals) * 100
                    : 0;
                return renderRateBar(rate);
            }
        },
        {
            title: renderSortTitle('latestDeliveryRate', t('wms.delivery.parcelSlaMonitoring.columns.latestDeliverySuccessRate')),
            dataIndex: 'latestDeliveryRate',
            key: 'latestDeliveryRate',
            width: 80,
            align: 'left' as const,
            render: (rate: number) => renderRateBar(rate)
        },
        // 未妥投总数 = 4 大类之和；warehouseSide 已含 other，勿再叠加
        // 数字可点击 → 打开 Drawer，状态预填「未妥投」（4 个一级归因任一命中，与本列同口径）
        {
            title: t('wms.delivery.parcelSlaMonitoring.columns.undeliveredTotal'),
            key: 'undeliveredTotal',
            width: 90,
            align: 'left' as const,
            render: (_: unknown, record: SlaTableRow) => {
                const total = undeliveredTotal(record);
                return total > 0 ? (
                    <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); onDetailClick('v2:ALL', record); }}
                        style={{ color: '#1890ff' }}
                    >
                        {total}
                    </a>
                ) : (
                    <span>0</span>
                );
            }
        },
        // 12 子列 —— 按 L1 分组，每个 L1 作为父列（antd children），一级用配色标签、二级为纯文字
        // L1 组分隔：只加在 L1 父表头 <th> 的左侧（onHeaderCell 只挂在父列，不下发到 L2 子表头，也不进 body）
        ...LEVEL1_ORDER.map((l1) => {
            return {
                title: (
                    <span style={{ color: LEVEL1_META[l1].color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {getLevel1Label(t, l1)}
                    </span>
                ),
                key: `undelivered_group_${l1}`,
                onHeaderCell: () => ({ style: { borderLeft: '2px solid #d0d5dd' } }),
                children: LEVEL2_BY_LEVEL1[l1].map((l2) => {
                    const l2Meta = LEVEL2_META[l2];
                    return {
                        title: <span style={{ whiteSpace: 'nowrap' }}>{getLevel2LabelInGroup(t, l2)}</span>,
                        key: `undelivered_${l2}`,
                        width: 110,
                        align: 'left' as const,
                        render: (_: unknown, record: SlaTableRow) => {
                            const value = (record[l2Meta.field] as number | undefined) ?? 0;
                            return value > 0 ? (
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); onDetailClick(encodeUndeliveredAnomaly(l1, l2), record); }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {value}
                                </a>
                            ) : (
                                <span>{value}</span>
                            );
                        },
                    };
                }),
            };
        }),
        // 时区为美西时不展示 T+1～T+5 领取妥投率列
        ...(showT1ToT5 ? [
            {
                title: renderSortTitle('t1DeliveryRate', t('wms.delivery.parcelSlaMonitoring.columns.t1DeliveryRate')),
                dataIndex: 't1DeliveryRate',
                key: 't1DeliveryRate',
                width: 105,
                align: 'left' as const,
                render: (rate: number, record: SlaTableRow) => renderTNDeliveryRate(rate, record, 1)
            },
            {
                title: renderSortTitle('t2DeliveryRate', t('wms.delivery.parcelSlaMonitoring.columns.t2DeliveryRate')),
                dataIndex: 't2DeliveryRate',
                key: 't2DeliveryRate',
                width: 105,
                align: 'left' as const,
                render: (rate: number, record: SlaTableRow) => renderTNDeliveryRate(rate, record, 2)
            },
            {
                title: renderSortTitle('t3DeliveryRate', t('wms.delivery.parcelSlaMonitoring.columns.t3DeliveryRate')),
                dataIndex: 't3DeliveryRate',
                key: 't3DeliveryRate',
                width: 105,
                align: 'left' as const,
                render: (rate: number, record: SlaTableRow) => renderTNDeliveryRate(rate, record, 3)
            },
            {
                title: renderSortTitle('t4DeliveryRate', t('wms.delivery.parcelSlaMonitoring.columns.t4DeliveryRate')),
                dataIndex: 't4DeliveryRate',
                key: 't4DeliveryRate',
                width: 105,
                align: 'left' as const,
                render: (rate: number, record: SlaTableRow) => renderTNDeliveryRate(rate, record, 4)
            },
            {
                title: renderSortTitle('t5DeliveryRate', t('wms.delivery.parcelSlaMonitoring.columns.t5DeliveryRate')),
                dataIndex: 't5DeliveryRate',
                key: 't5DeliveryRate',
                width: 105,
                align: 'left' as const,
                render: (rate: number, record: SlaTableRow) => renderTNDeliveryRate(rate, record, 5)
            }
        ] : [])
    ];

    return (
        <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            size="middle"
            tableLayout="auto"
            pagination={false}
         className="mb-4"/>
    );
};

export default SlaMonitoringTable;
